/**
 * useFontPreload - Hook React para pré-carregar fontes PDF
 *
 * Use este hook no componente principal (App.tsx) para iniciar
 * o preload de fontes em background
 */

import { useEffect, useState } from 'react'
import { preloadCommonFonts, getPreloadStats } from '../utils/fontPreloader'

interface FontPreloadStats {
  isPreloading: boolean
  totalPreloaded: number
  totalFailed: number
  duration: number | null
  preloadedFonts: string[]
  failedFonts: string[]
}

// Flag global para prevenir múltiplas execuções (React StrictMode em desenvolvimento)
let preloadInitiated = false

export function useFontPreload(options: {
  enabled?: boolean
  delay?: number
} = {}) {
  const { enabled = true, delay = 2000 } = options

  const [stats, setStats] = useState<FontPreloadStats>({
    isPreloading: false,
    totalPreloaded: 0,
    totalFailed: 0,
    duration: null,
    preloadedFonts: [],
    failedFonts: []
  })

  useEffect(() => {
    if (!enabled) {
      return
    }

    // Prevenir logs duplicados em React StrictMode (desenvolvimento)
    if (preloadInitiated) {
      return
    }

    // Aguardar um pouco para não bloquear inicialização da aplicação
    const timer = setTimeout(async () => {
      // Verificar novamente antes de executar
      if (preloadInitiated) {
        return
      }

      preloadInitiated = true

      try {
        setStats(prev => ({ ...prev, isPreloading: true }))

        // Carregar fontes mais comuns (Liberation + DejaVu)
        await preloadCommonFonts()

        // Atualizar estatísticas
        const newStats = getPreloadStats()
        setStats({
          isPreloading: newStats.isPreloading,
          totalPreloaded: newStats.totalPreloaded,
          totalFailed: newStats.totalFailed,
          duration: newStats.duration,
          preloadedFonts: newStats.preloadedFonts,
          failedFonts: newStats.failedFonts
        })

      } catch (error) {
        setStats(prev => ({ ...prev, isPreloading: false }))
      }
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [enabled, delay])

  return stats
}

export default useFontPreload
